import { Component, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { fb, routeParams } from '../../../../../modules/shared/shared.global-service';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TextInputComponent } from '../../../../kit/input/text-input/text-input.component';
import { RadioInputComponent } from '../../../../kit/input/radio-input/radio-input.component';
import { ActionButtonsComponent } from '../../../../kit/action-buttons/action-buttons.component';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { dateifyOrNow } from '../../../../../modules/shared/helpers/date.helper';
import { Validators } from '@angular/forms';
import { FolderSickNotesDoctorRequestModalStore } from './folder-sick-notes-doctor-request-modal.store';
import { csszDateFormat } from '../utils-CSSZ';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { FileUpload, FileUploadModule } from '@openng/optimus-ui/fileupload';
import {
    CSSZFile,
    FolderSickNotesZolusouInput,
} from '@libs/types/src/services/input-type/cssz/folder-sick-notes.zolusou-input';
import { CsszSickNoteFillingTypeEnum } from '@libs/types/src/services/enum/cssz/cssz-sick-note-filling-type.enum';
import { AddressDetailObject, OkObject } from '@it2go/types';
import { AddressInputComponent } from '../../../../kit/input/address-input/address-input.component';

@Component({
    selector: 'app-folder-sick-notes-doctor-request-modal',
    templateUrl: 'folder-sick-notes-doctor-request-modal.component.html',
    styleUrls: ['folder-sick-notes-doctor-request-modal.component.scss'],
    imports: [
        DateInputComponent,
        TranslatePipe,
        TextInputComponent,
        RadioInputComponent,
        ActionButtonsComponent,
        FileUploadModule,
        ButtonComponent,
        AddressInputComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [FolderSickNotesDoctorRequestModalStore],
})
export class FolderSickNotesDoctorRequestModalComponent extends BaseDialogComponent {
    public readonly store = inject(FolderSickNotesDoctorRequestModalStore);
    @ViewChild('fu') fileUpload!: FileUpload;

    protected form = fb.group({
        type: fb.control<CsszSickNoteFillingTypeEnum | null>(null),

        issueDate: fb.control<Date>(dateifyOrNow(), Validators.required),
        reason: fb.control<string | null>(null, Validators.required),
    });

    protected zolusuForm = fb.group({
        issuedToDate: fb.control<Date | null>(null, Validators.required),
    });

    protected zuspvyForm = fb.group({
        walkoutsFromDate: fb.control<Date | null>(null, Validators.required),
        walkoutsToDate: fb.control<Date | null>(null),
    });

    protected zpnpusForm = fb.group({
        stayStartDate: fb.control<Date | null>(null, Validators.required),
        stayEndDate: fb.control<Date | null>(null),
        address: fb.control<AddressDetailObject | null>(null, Validators.required),
    });

    protected prefix = 'sickNotes.doctorRequest.form.';
    protected typesPrefix = 'sickNotes.doctorRequest.types.';

    protected types = [
        { value: CsszSickNoteFillingTypeEnum.ZOLUSU, name: CsszSickNoteFillingTypeEnum.ZOLUSU },
        { value: CsszSickNoteFillingTypeEnum.ZOLUSOU, name: CsszSickNoteFillingTypeEnum.ZOLUSOU },
        { value: CsszSickNoteFillingTypeEnum.ZUSPVY, name: CsszSickNoteFillingTypeEnum.ZUSPVY },
        { value: CsszSickNoteFillingTypeEnum.ZPNPUS, name: CsszSickNoteFillingTypeEnum.ZPNPUS },
    ];

    protected async submit() {
        if (this.form.isInvalid() || !this.store.currentUser()?.username)
            return;

        const files: CSSZFile[] = [];
        if (this.fileUpload.files) {
            for (const file of this.fileUpload.files) {
                const base64 = await this.fileToBase64(file);
                files.push({
                    fileName: file.name,
                    fileContent: base64,
                    mimeType: file.type,
                    fileSize: file.size?.toString(),
                });
            }
        }

        const base: FolderSickNotesZolusouInput = {
            sickNoteId: +routeParams.sickNoteId()!,
            issueDate: csszDateFormat(this.form.value.issueDate)!,
            reason: this.form.value.reason!,
            doctorName: this.store.currentUser()!.username,
            files: files,
        };

        const type = this.form.value.type;
        if (!type) return;

        let res: OkObject | undefined;
        switch (type) {
            case CsszSickNoteFillingTypeEnum.ZOLUSU:
                res = await this.sendZolusu(base);
                break;
            case CsszSickNoteFillingTypeEnum.ZOLUSOU:
                res = await this.store.api
                    .updateZolusou(base);
                break;
            case CsszSickNoteFillingTypeEnum.ZUSPVY:
                res = await this.sendZuspvy(base);
                break;
            case CsszSickNoteFillingTypeEnum.ZPNPUS:
                res = await this.sendZpnpus(base);
                break;
        }

        if (res?.ok) {
            contentGlobalSuccess.next('common.created');
            this.dialogRef.close();
        }
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                let base64String = reader.result as string;
                base64String = base64String.split(',')[1];
                resolve(base64String);
            };
            reader.onerror = (error) => reject(error);
        }) as unknown as Promise<string>;
    }

    private async sendZolusu(common: FolderSickNotesZolusouInput) {
        if (this.zolusuForm.isInvalid()) return;
        return this.store.api
            .updateZolusu({
                ...common,
                issuedToDate: csszDateFormat(this.zolusuForm.value.issuedToDate)!,
            });
    }

    private async sendZuspvy(common: FolderSickNotesZolusouInput) {
        if (this.zuspvyForm.isInvalid()) return;
        return this.store.api
            .updateZuspvy({
                ...common,
                walkoutsFromDate: csszDateFormat(this.zuspvyForm.value.walkoutsFromDate)!,
                walkoutsToDate: csszDateFormat(this.zuspvyForm.value.walkoutsToDate),
            });
    }

    private sendZpnpus(common: FolderSickNotesZolusouInput) {
        if (this.zpnpusForm.isInvalid()) return;
        return this.store.api
            .updateZpnpus({
                ...common,
                stayStartDate: csszDateFormat(this.zpnpusForm.value.stayStartDate)!,
                stayEndDate: csszDateFormat(this.zpnpusForm.value.stayEndDate),
                address: this.zpnpusForm.value.address!,
            });
    }

    protected readonly CsszSickNoteFillingTypeEnum = CsszSickNoteFillingTypeEnum;
}
